export const SPONSORSHIP_TYPE_NONE = 'none';

export interface SponsorshipTypeConfig {
  name: string;
  value: string;
  platforms?: string[];
}

export const DEFAULT_SPONSORSHIP_TYPES: SponsorshipTypeConfig[] = [
  { name: 'Solo Premio', value: 'rewards_only', platforms: ['voletaje'] },
  { name: 'Solo Branding', value: 'branding_only', platforms: ['voletaje'] },
  { name: 'Premio y Branding', value: 'custom', platforms: ['voletaje'] },
];

const KNOWN_TYPE_I18N_KEYS = new Set([
  'rewards_only',
  'branding_only',
  'custom',
  'full_entrance',
  'partial_entrance',
]);

export function normalizeSponsorshipTypes(types: unknown): SponsorshipTypeConfig[] {
  if (!Array.isArray(types)) return [];
  return types.map((type: any) => ({
    name: type.name || type.value || '',
    value: type.value || '',
    platforms: Array.isArray(type.platforms) ? type.platforms : [],
  }));
}

export function translateSponsorshipTypeLabel(
  type: SponsorshipTypeConfig,
  t: (key: string) => string
): string {
  if (KNOWN_TYPE_I18N_KEYS.has(type.value)) {
    return t(`type_and_budget_info_sponsorship.${type.value}`);
  }
  return type.name || type.value;
}

export function getFallbackTypeOptions(t: (key: string) => string): SponsorshipTypeConfig[] {
  return DEFAULT_SPONSORSHIP_TYPES.map((type) => ({
    ...type,
    name: translateSponsorshipTypeLabel(type, t),
  }));
}

export function getAvailableSponsorshipTypes(
  config: SponsorshipTypeConfig[],
  loading: boolean,
  t: (key: string) => string
): SponsorshipTypeConfig[] {
  if (loading) return [];
  if (config.length > 0) return config;
  return getFallbackTypeOptions(t);
}

export async function fetchSponsorshipTypes(
  apiBaseUrl: string,
  headers: Record<string, string>,
  t?: (key: string) => string
): Promise<SponsorshipTypeConfig[]> {
  try {
    const response = await fetch(`${apiBaseUrl}/platforms/types/sponsorship`, { headers });
    if (!response.ok) throw new Error('Failed to load sponsorship types');
    const types = normalizeSponsorshipTypes(await response.json());
    if (t) {
      return types.map((type) => ({
        ...type,
        name: translateSponsorshipTypeLabel(type, t),
      }));
    }
    return types;
  } catch {
    return t ? getFallbackTypeOptions(t) : DEFAULT_SPONSORSHIP_TYPES;
  }
}

/** Normaliza valores legacy del campo sponsorship_type en concursos. */
export function normalizeContestSponsorshipType(
  value: string | null | undefined,
  availableValues: string[]
): string {
  if (!value || value === 'none') return SPONSORSHIP_TYPE_NONE;
  if (value === 'sponsored') {
    return availableValues.includes('rewards_only') ? 'rewards_only' : availableValues[0] || SPONSORSHIP_TYPE_NONE;
  }
  if (availableValues.includes(value)) return value;
  return SPONSORSHIP_TYPE_NONE;
}

export function resolveSponsorBillToEmail(sponsorship: {
  contact_email?: string;
  sponsor_email?: string;
}): string {
  return (sponsorship.contact_email || sponsorship.sponsor_email || '').trim();
}

export function extractPaylinkFromResponse(result: unknown): string | null {
  if (!result || typeof result !== 'object') return null;
  const root = result as Record<string, any>;
  const candidates = [
    root.paylink,
    root.data?.paylink,
    root.result?.paylink,
    root.result?.data?.paylink,
    root.results?.[0]?.paylink,
    root.results?.[0]?.data?.paylink,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }
  return null;
}

export function extractInvoiceNoFromPaylinkResponse(result: unknown): string | null {
  if (!result || typeof result !== 'object') return null;
  const root = result as Record<string, any>;
  const dataNodes = [root.data, root.result?.data, root.results?.[0]?.data, root.results?.[0]];
  for (const node of dataNodes) {
    if (!node || typeof node !== 'object') continue;
    const invoiceNo = node.invoice_no || node.invoiceNo;
    if (invoiceNo != null && String(invoiceNo).trim()) {
      return String(invoiceNo);
    }
  }
  return null;
}

export function extractCostDescriptionFromPaylinkResponse(result: unknown): any[] | null {
  if (!result || typeof result !== 'object') return null;
  const root = result as Record<string, any>;
  const payloadNodes = [root.payload, root.results?.[0]?.payload, root.result?.payload];
  for (const node of payloadNodes) {
    if (node?.items && Array.isArray(node.items)) {
      return node.items;
    }
  }
  return null;
}
